import {
  ATIVOSBASE_LIST,
  ATIVOSBASE_MERCADO,
  ATIVOSBASE_COTACAO,
  ATIVOSBASE_READ,
  ATIVOSBASE_CURVA,
  ATIVOSBASE_AVALIACAO
} from '../const/servicesURI';
import requestService from './requestService';
import { httpRequests } from 'investira.sdk';
import utils from '../utils';

const cancel = {
  read: null,
  readByKey: {},
  list: null,
  mercado: null,
  curva: null,
  cotacao: null,
  cotacaoByKey: {}
};

/**
 * Resolve uma chave estável para isolar requests concorrentes do mesmo método.
 *
 * @param {Object} pProps Parâmetros recebidos pelo service.
 * @returns {string} Chave explícita ou identificador do ativo.
 */
function pvGetRequestKey(pProps = {}) {
  return String(pProps.requestKey || pProps.ifeed_ativo_id || pProps.ativo_id || 'default');
}

/**
 * Cancela somente a leitura identificada, preservando os demais fundos.
 *
 * @param {string|number} pRequestKey Chave da requisição.
 */
export const cancelRead = pRequestKey => {
  const xRequestKey = String(pRequestKey || 'default');
  const xCancelToken = cancel.readByKey[xRequestKey];

  if (xCancelToken) {
    xCancelToken.cancel();
    delete cancel.readByKey[xRequestKey];
  }
};

/** Cancela todas as leituras ainda registradas. */
export const cancelAllReads = () => {
  Object.keys(cancel.readByKey).forEach(cancelRead);
};

/**
 * Cancela somente a cotação identificada, preservando os demais fundos.
 *
 * @param {string|number} pRequestKey Chave da requisição.
 */
export const cancelCotacao = pRequestKey => {
  const xRequestKey = String(pRequestKey || 'default');
  const xCancelToken = cancel.cotacaoByKey[xRequestKey];

  if (xCancelToken) {
    xCancelToken.cancel();
    delete cancel.cotacaoByKey[xRequestKey];
  }
};

/** Cancela todas as cotações ainda registradas. */
export const cancelAllCotacoes = () => {
  Object.keys(cancel.cotacaoByKey).forEach(cancelCotacao);
};

export const read = (pProps, pResolve, pReject, pFinally) => {
  const xRequestKey = pvGetRequestKey(pProps);

  cancelRead(xRequestKey);

  cancel.read = httpRequests.cancelToken();
  cancel.readByKey[xRequestKey] = cancel.read;
  const xCancelToken = cancel.read;

  const xProps = {
    params: pProps.params,
    cancelToken: cancel.read.token
  };

  return requestService(xProps, ATIVOSBASE_READ + pProps.ifeed_ativo_id, 3)
    .then(rRes => {
      return pResolve && pResolve(rRes);
    })
    .catch(rErr => {
      utils.request.tracer(rErr, 'Error Service AtivosBase Read');
      return pReject && pReject(rErr);
    })
    .finally(() => {
      if (cancel.readByKey[xRequestKey] === xCancelToken) {
        delete cancel.readByKey[xRequestKey];
      }

      pFinally && pFinally();
    });
};

export const list = (pParams, pResolve, pReject, pFinally) => {
  if (cancel.list) {
    cancel.list.cancel();
  }

  cancel.list = httpRequests.cancelToken();

  const xProps = {
    params: pParams,
    cancelToken: cancel.list.token
  };

  return requestService(xProps, ATIVOSBASE_LIST, 3)
    .then(rRes => {
      return pResolve && pResolve(rRes);
    })
    .catch(rErr => {
      utils.request.tracer(rErr, 'Error Service AtivosBase List');
      return pReject && pReject(rErr);
    })
    .finally(() => {
      pFinally && pFinally();
    });
};

export const cotacao = (pParams, pResolve, pReject, pFinally) => {
  const xRequestKey = pvGetRequestKey(pParams);
  const xRequestParams = { ...pParams };

  delete xRequestParams.requestKey;

  cancelCotacao(xRequestKey);

  cancel.cotacao = httpRequests.cancelToken();
  cancel.cotacaoByKey[xRequestKey] = cancel.cotacao;
  const xCancelToken = cancel.cotacao;

  const xParams = {
    ...xRequestParams,
    sort: 'data'
  };

  const xProps = {
    params: xParams,
    cancelToken: cancel.cotacao.token
  };

  return requestService(xProps, ATIVOSBASE_COTACAO, 3)
    .then(rRes => {
      return pResolve && pResolve(rRes);
    })
    .catch(rErr => {
      utils.request.tracer(rErr, 'Error Service Ativos Cotação');
      return pReject && pReject(rErr);
    })
    .finally(() => {
      if (cancel.cotacaoByKey[xRequestKey] === xCancelToken) {
        delete cancel.cotacaoByKey[xRequestKey];
      }

      pFinally && pFinally();
    });
};

export const mercado = (pParams, pResolve, pReject, pFinally) => {
  if (cancel.list) {
    cancel.list.cancel();
  }

  cancel.list = httpRequests.cancelToken();

  const xProps = {
    params: pParams,
    cancelToken: cancel.list.token
  };

  return requestService(xProps, ATIVOSBASE_MERCADO, 3)
    .then(rRes => {
      return pResolve && pResolve(rRes);
    })
    .catch(rErr => {
      utils.request.tracer(rErr, 'Error Service AtivosBase Mercado');
      return pReject && pReject(rErr);
    })
    .finally(() => {
      pFinally && pFinally();
    });
};

export const curva = (pParams, pResolve, pReject, pFinally) => {
  if (cancel.curva) {
    cancel.curva.cancel();
  }

  cancel.curva = httpRequests.cancelToken();

  const xProps = {
    params: pParams,
    cancelToken: cancel.curva.token
  };

  return requestService(xProps, ATIVOSBASE_CURVA, 3)
    .then(rRes => {
      return pResolve && pResolve(rRes);
    })
    .catch(rErr => {
      utils.request.tracer(rErr, 'Error Service AtivosBase Curva');
      return pReject && pReject(rErr);
    })
    .finally(() => {
      pFinally && pFinally();
    });
};

export const avaliacao = (pParams, pResolve, pReject, pFinally) => {
  const xProps = {
    params: pParams
  };

  return requestService(xProps, ATIVOSBASE_AVALIACAO, 3)
    .then(rRes => {
      return pResolve && pResolve(rRes);
    })
    .catch(rErr => {
      utils.request.tracer(rErr, 'Error Service AtivosBase Avaliacao');
      return pReject && pReject(rErr);
    })
    .finally(() => {
      pFinally && pFinally();
    });
};

const ativosbase = {
  list,
  read,
  cotacao,
  mercado,
  curva,
  avaliacao,
  cancelRead,
  cancelAllReads,
  cancelCotacao,
  cancelAllCotacoes,
  cancel
};

export default ativosbase;
